import { Conversation, ConversationKind, ConversationParticipantRef, Message } from '../types/Conversation';
import { getConversationLastActivityIso, idsEqual, upsertAndSortMessages } from './messageUtils';

export interface ConversationListItemLastMessage {
  id: string;
  sender: ConversationParticipantRef;
  content: string;
  timestamp: string;
  status: Message['status'];
  source: string;
  messageType: string;
  channelContext?: Message['channelContext'];
}

export interface ConversationListItemRealtimePayload {
  conversationId: string;
  item: {
    title: string | null;
    updatedAt: string;
    isSigned: boolean;
    isAiAssistantMuted: boolean;
    isFavorite: boolean;
    isPinned: boolean;
    isSpam: boolean;
    isHidden: boolean;
    note: string | null;
    assignedToEmployeeId: string | null;
    assignedToEmployeeName: string | null;
    lastMessage: ConversationListItemLastMessage | null;
    hasExternalParticipant: boolean;
    conversationKind: ConversationKind;
    containsWhatsAppMessages: boolean;
    containsInstagramMessages: boolean;
    containsInstagramComments: boolean;
    containsMessengerComments: boolean;
  };
}

export const inferContactChannelTypeFromMessageSource = (
  source?: string,
): ConversationParticipantRef['contactChannelType'] | undefined => {
  switch ((source || '').trim().toLowerCase()) {
    case 'whatsapp':
      return 'whatsApp';
    case 'telegram':
      return 'telegram';
    case 'instagram':
      return 'instagram';
    case 'messenger':
      return 'messenger';
    case 'tiktok':
      return 'tikTok';
    case 'webchat':
      return 'other';
    case 'voice':
    case 'phone':
      return 'phone';
    default:
      return undefined;
  }
};

export const enrichParticipantFromMessageSource = (
  participant: ConversationParticipantRef,
  source?: string,
): ConversationParticipantRef => {
  if (participant.contactChannelType) {
    return participant;
  }

  const inferredChannelType = inferContactChannelTypeFromMessageSource(source);
  if (!inferredChannelType) {
    return participant;
  }

  return {
    ...participant,
    contactChannelType: inferredChannelType,
    contactChannelLabel: participant.contactChannelLabel
      ?? (inferredChannelType === 'other' && (source || '').trim().toLowerCase() === 'webchat' ? 'webchat' : undefined),
  };
};

export const listItemLastMessageToMessage = (
  conversationId: string,
  lastMessage: ConversationListItemLastMessage | null,
): Message | null => {
  if (!lastMessage?.id) {
    return null;
  }

  return {
    id: lastMessage.id,
    conversationId,
    sender: enrichParticipantFromMessageSource(lastMessage.sender, lastMessage.source),
    content: lastMessage.content,
    timestamp: lastMessage.timestamp,
    status: lastMessage.status,
    source: lastMessage.source,
    messageType: lastMessage.messageType,
    channelContext: lastMessage.channelContext,
    readBy: [],
    reactions: {},
  };
};

const seedParticipantsFromListItem = (
  item: ConversationListItemRealtimePayload['item'],
): ConversationParticipantRef[] => {
  if (!item.lastMessage) {
    return [];
  }

  const sender = enrichParticipantFromMessageSource(item.lastMessage.sender, item.lastMessage.source);
  if (sender.type === 'customer' || sender.type === 'lead') {
    return [sender];
  }

  return [];
};

const resolveParticipants = (
  existing: Conversation | undefined,
  item: ConversationListItemRealtimePayload['item'],
): ConversationParticipantRef[] => {
  if (existing?.participants.length) {
    return existing.participants.map(participant => {
      if (!item.lastMessage || (participant.type !== 'customer' && participant.type !== 'lead')) {
        return participant;
      }

      return enrichParticipantFromMessageSource(participant, item.lastMessage.source);
    });
  }

  return seedParticipantsFromListItem(item);
};

const resolveMessages = (
  existing: Conversation | undefined,
  conversationId: string,
  item: ConversationListItemRealtimePayload['item'],
): Message[] => {
  const incomingMessage = listItemLastMessageToMessage(conversationId, item.lastMessage);
  if (!incomingMessage) {
    return existing?.messages ?? [];
  }

  return upsertAndSortMessages(existing?.messages ?? [], incomingMessage);
};

export const mergeConversationWithListItemEvent = (
  existing: Conversation | undefined,
  payload: ConversationListItemRealtimePayload,
): Conversation => {
  const { conversationId, item } = payload;
  const messages = resolveMessages(existing, conversationId, item);
  const participants = resolveParticipants(existing, item);

  return {
    id: conversationId,
    title: item.title || existing?.title || undefined,
    participants,
    messages,
    createdAt: existing?.createdAt ?? item.updatedAt,
    updatedAt: getConversationLastActivityIso({ messages, updatedAt: item.updatedAt }),
    isSigned: item.isSigned,
    isAiAssistantMuted: item.isAiAssistantMuted,
    isFavorite: item.isFavorite,
    isPinned: item.isPinned,
    isSpam: Boolean(item.isSpam ?? item.isHidden),
    isHidden: Boolean(item.isSpam ?? item.isHidden),
    note: item.note || existing?.note || undefined,
    notes: existing?.notes ?? [],
    visibleNoteCount: item.note
      ? Math.max(existing?.visibleNoteCount ?? 0, 1)
      : (existing?.visibleNoteCount ?? 0),
    assignedToEmployeeId: item.assignedToEmployeeId || existing?.assignedToEmployeeId || undefined,
    assignedToEmployeeName: item.assignedToEmployeeName || existing?.assignedToEmployeeName || undefined,
    hasExternalParticipant: item.hasExternalParticipant,
    conversationKind: item.conversationKind,
    containsWhatsAppMessages: item.containsWhatsAppMessages,
    containsInstagramMessages: item.containsInstagramMessages,
    containsInstagramComments: item.containsInstagramComments,
    containsMessengerComments: item.containsMessengerComments,
    totalMessageCount: existing?.totalMessageCount,
    hasMoreMessages: existing?.hasMoreMessages,
  };
};

export const upsertConversationListItem = (
  conversations: Conversation[],
  payload: ConversationListItemRealtimePayload,
): Conversation[] => {
  const index = conversations.findIndex(conversation => idsEqual(conversation.id, payload.conversationId));
  const merged = mergeConversationWithListItemEvent(index === -1 ? undefined : conversations[index], payload);

  if (index === -1) {
    return [...conversations, merged];
  }

  const next = [...conversations];
  next[index] = merged;
  return next;
};
